import React, { useEffect, useId, useRef, useState } from 'react';
import type { TimePickerProps } from './types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m ?? 0);
}

function fromMinutes(mins: number): string {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function formatDisplay(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${period}`;
}

function buildSlots(step: number, min?: string, max?: string): string[] {
  const slots: string[] = [];
  const minMins = min ? toMinutes(min) : 0;
  const maxMins = max ? toMinutes(max) : 23 * 60 + 59;
  for (let m = 0; m < 24 * 60; m += step) {
    if (m >= minMins && m <= maxMins) slots.push(fromMinutes(m));
  }
  return slots;
}

// ─── ClockIcon ────────────────────────────────────────────────────────────────

function ClockIcon() {
  return (
    <svg
      className="h-4 w-4 text-neutral-400 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

// ─── TimePicker ───────────────────────────────────────────────────────────────

export function TimePicker({
  value = '',
  onChange,
  label,
  min,
  max,
  step = 15,
  error,
  disabled = false,
  timezone = 'IST',
}: TimePickerProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const listboxId = `${id}-listbox`;

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const slots = buildSlots(step, min, max);
  const selectedIndex = value ? slots.indexOf(value) : -1;

  // Scroll selected option into view when opening
  useEffect(() => {
    if (!open || !listboxRef.current) return;
    const idx = selectedIndex >= 0 ? selectedIndex : 0;
    const item = listboxRef.current.children[idx] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
    setActiveIndex(idx);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus active option when it changes
  useEffect(() => {
    if (!open || activeIndex < 0 || !listboxRef.current) return;
    const item = listboxRef.current.children[activeIndex] as HTMLElement | undefined;
    item?.focus();
  }, [activeIndex, open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (
        listboxRef.current && !listboxRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  function handleTriggerKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(true);
    }
  }

  function handleListKeyDown(e: React.KeyboardEvent) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, slots.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(slots.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (activeIndex >= 0) {
          onChange?.(slots[activeIndex]);
          setOpen(false);
          triggerRef.current?.focus();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        break;
    }
  }

  const hasError = Boolean(error);

  const triggerClasses = [
    'flex items-center gap-2 w-full border rounded bg-white h-11 px-3 text-sm transition-colors duration-150',
    'dark:bg-neutral-800 dark:text-neutral-100',
    hasError
      ? 'border-danger-500 focus:ring-2 focus:ring-danger-500 focus:ring-offset-0 focus:outline-none'
      : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:ring-offset-0 focus:outline-none',
    disabled ? 'bg-neutral-100 dark:bg-neutral-700 opacity-70 cursor-not-allowed pointer-events-none' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1 relative">
      {label && (
        <label
          htmlFor={`${id}-trigger`}
          className={[
            'text-sm font-medium leading-none',
            hasError ? 'text-danger-700 dark:text-danger-400' : 'text-neutral-700 dark:text-neutral-300',
          ].join(' ')}
        >
          {label}
        </label>
      )}

      <button
        ref={triggerRef}
        id={`${id}-trigger`}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={`${label ?? 'Time'} (${timezone})`}
        aria-describedby={hasError ? errorId : undefined}
        aria-invalid={hasError || undefined}
        disabled={disabled}
        className={triggerClasses}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={handleTriggerKeyDown}
      >
        <ClockIcon />
        <span className={value ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-400'}>
          {value ? formatDisplay(value) : `HH:MM ${timezone}`}
        </span>
        <span className="ml-auto text-xs text-neutral-400">{timezone}</span>
      </button>

      {hasError && (
        <span id={errorId} role="alert" className="text-xs text-danger-600 dark:text-danger-400">
          {error}
        </span>
      )}

      {/* Dropdown listbox */}
      {open && (
        <ul
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label={`${label ?? 'Time'} options (${timezone})`}
          className="absolute top-full left-0 mt-1 z-[60] w-full max-h-48 overflow-y-auto bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg py-1 focus:outline-none"
          tabIndex={-1}
          onKeyDown={handleListKeyDown}
        >
          {slots.map((slot, i) => {
            const isSelected = slot === value;
            const isActive = i === activeIndex;
            return (
              <li
                key={slot}
                role="option"
                aria-selected={isSelected}
                tabIndex={isActive ? 0 : -1}
                className={[
                  'px-3 py-2 text-sm cursor-pointer transition-colors select-none focus:outline-none',
                  isSelected
                    ? 'bg-brand-500 text-white font-medium'
                    : isActive
                      ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100'
                      : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                ].filter(Boolean).join(' ')}
                onClick={() => {
                  onChange?.(slot);
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
                onMouseEnter={() => setActiveIndex(i)}
              >
                {formatDisplay(slot)}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
